/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, CreateAdminOauthClientResponse, CreateM2MClientRequest } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';

export type CreateM2MClientMutationConfig = {
  onSuccess?: (data: CreateAdminOauthClientResponse, variables: CreateM2MClientRequest) => void;
  onError?: (error: unknown, variables: CreateM2MClientRequest) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
};

/**
 * Hook for creating a new M2M client through the admin OAuth clients API.
 *
 * @param config.onSuccess - Called after successful M2M client creation
 * @param config.onError - Called if creation fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for creating M2M clients
 */
export function useCreateM2MClientMutation(config: CreateM2MClientMutationConfig) {
  const queryClient = useQueryClient();

  const { mutate, isError, error, isPending } = useMutation<
    CreateAdminOauthClientResponse,
    ApiError,
    CreateM2MClientRequest
  >({
    mutationFn: (client: CreateM2MClientRequest) => {
      return mgmtPlaneAPIClient.adminOauthClients.createAdminOauthClient({
        requestBody: {
          name: client.name,
          allowedScopes: client.allowedScopes,
        },
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['m2mClients'] });
      config?.onSuccess?.(data, variables);
    },
    onError: config?.onError,
    onSettled: config?.onSettled,
  });

  return { mutate, isError, error, isPending };
}
